import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Check, X, ShieldCheck, ShieldOff, AlertTriangle, RefreshCw, Users as UsersIcon, Crown,
} from "lucide-react";

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "revoked", label: "Revoked" },
  { key: "all", label: "All" },
];

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function Users() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("pending");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { action, user }
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab !== "all") params.status = tab;
      const r = await api.get("/admin/users", { params });
      setUsers(r.data.items);
    } catch (e) {
      showToast("Failed to load users", "err");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const performAction = async () => {
    if (!confirm) return;
    const { action, user } = confirm;
    // Close the dialog immediately for fast feedback
    setConfirm(null);
    // Optimistic UI: if approving from Pending tab, revoking from Approved tab, etc — the row should disappear instantly
    const stillFitsTab = (newStatus, newRole) => {
      if (tab === "all") return true;
      if (tab === "pending") return newStatus === "pending";
      if (tab === "approved") return newStatus === "approved";
      if (tab === "revoked") return newStatus === "revoked";
      return true;
    };
    const optimistic = users.map((u) => {
      if (u.user_id !== user.user_id) return u;
      if (action === "approve") return { ...u, status: "approved" };
      if (action === "revoke") return { ...u, status: "revoked" };
      if (action === "promote") return { ...u, role: "admin" };
      if (action === "demote") return { ...u, role: "user" };
      return u;
    });
    // Drop rows that no longer match the current tab
    const filtered = optimistic.filter((u) => stillFitsTab(u.status, u.role));
    setUsers(filtered);
    try {
      await api.post(`/admin/users/${user.user_id}/${action}`);
      showToast(`${user.name || user.email} — ${action}d`);
      // Authoritative refetch to reconcile any divergence
      await fetchUsers();
    } catch (e) {
      const msg = e?.response?.data?.detail || `${action} failed`;
      showToast(msg, "err");
      // On failure, refetch to restore the row
      await fetchUsers();
    }
  };

  const counts = STATUS_TABS.reduce((acc, t) => { acc[t.key] = 0; return acc; }, {});
  // We won't fetch counts separately to keep it light — the tab is the source of truth.

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">User Administration</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight leading-tight">
            Approve who gets aboard.
          </h1>
          <p className="mt-2 text-slate-600">Approve, revoke, and manage admin permissions for everyone who signs in.</p>
        </div>
        <button
          type="button"
          onClick={fetchUsers}
          data-testid="users-refresh-btn"
          className="self-start inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#0a2952] text-slate-700 px-4 py-2.5 rounded-md font-semibold transition-colors"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="users-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`users-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 -mb-[2px] transition-colors ${
              tab === t.key
                ? "border-[#ff5b2c] text-[#0a2952]"
                : "border-transparent text-slate-500 hover:text-[#0a2952]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Requested</th>
                <th className="px-4 py-3 text-left">Decided</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="users-loading">Loading users...</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center" data-testid="users-empty">
                  <div className="flex flex-col items-center gap-2 text-slate-500">
                    <UsersIcon size={32} className="text-slate-300" />
                    <span className="font-semibold">No users in this view.</span>
                  </div>
                </td></tr>
              )}
              {!loading && users.map((u) => {
                const isMe = u.user_id === me?.user_id;
                return (
                  <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#0a2952] text-white grid place-items-center text-sm font-semibold">
                            {(u.name || u.email)?.[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-semibold text-[#0a2952] truncate flex items-center gap-2">
                            {u.name || "—"}
                            {isMe && <span className="text-[9px] uppercase tracking-wider bg-[#ff5b2c]/10 text-[#ff5b2c] px-1.5 py-0.5 rounded">You</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700"><span className="font-mono-tabular text-xs">{u.email}</span></td>
                    <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold tracking-wide rounded-sm ${
                        u.role === "admin" ? "bg-[#ff5b2c]/10 text-[#ff5b2c]" : "bg-slate-100 text-slate-600"
                      }`}>
                        {u.role === "admin" && <Crown size={11} />}
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono-tabular">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono-tabular">{fmtDate(u.decided_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {u.status === "pending" && (
                          <button
                            type="button"
                            data-testid={`approve-btn-${u.user_id}`}
                            onClick={() => setConfirm({ action: "approve", user: u })}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-[#22c55e]/10 text-[#15803d] hover:bg-[#22c55e]/20"
                          >
                            <Check size={12} /> Approve
                          </button>
                        )}
                        {u.status === "approved" && !isMe && (
                          <>
                            {u.role === "user" ? (
                              <button
                                type="button"
                                data-testid={`promote-btn-${u.user_id}`}
                                onClick={() => setConfirm({ action: "promote", user: u })}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-[#0a2952]/5 text-[#0a2952] hover:bg-[#0a2952]/10"
                              >
                                <ShieldCheck size={12} /> Make admin
                              </button>
                            ) : (
                              <button
                                type="button"
                                data-testid={`demote-btn-${u.user_id}`}
                                onClick={() => setConfirm({ action: "demote", user: u })}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                              >
                                <ShieldOff size={12} /> Demote
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`revoke-btn-${u.user_id}`}
                              onClick={() => setConfirm({ action: "revoke", user: u })}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100"
                            >
                              <X size={12} /> Revoke
                            </button>
                          </>
                        )}
                        {u.status === "revoked" && (
                          <button
                            type="button"
                            data-testid={`reapprove-btn-${u.user_id}`}
                            onClick={() => setConfirm({ action: "approve", user: u })}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-semibold bg-[#22c55e]/10 text-[#15803d] hover:bg-[#22c55e]/20"
                          >
                            <Check size={12} /> Re-approve
                          </button>
                        )}
                        {isMe && u.status === "approved" && (
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">Self-managed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {confirm && (
        <ConfirmActionDialog
          action={confirm.action}
          user={confirm.user}
          onCancel={() => setConfirm(null)}
          onConfirm={performAction}
        />
      )}
      {toast && (
        <div
          data-testid="toast"
          className={`fixed top-20 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md shadow-xl text-sm font-semibold flex items-center gap-2 animate-[fadeIn_0.15s_ease-out] ${
            toast.kind === "err" ? "bg-red-600 text-white" : "bg-[#0a2952] text-white"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-50 text-amber-700",
    approved: "bg-[#22c55e]/10 text-[#15803d]",
    revoked: "bg-red-50 text-red-700",
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-bold tracking-wide rounded-sm ${styles[status] || "bg-slate-100 text-slate-600"}`}>
      {status?.toUpperCase()}
    </span>
  );
}

function ConfirmActionDialog({ action, user, onCancel, onConfirm }) {
  const config = {
    approve:  { title: "Approve user?",   desc: "They'll get immediate access to the dashboard and records.", btn: "Approve", color: "bg-[#22c55e] hover:bg-[#16a34a]", icon: <Check className="text-[#15803d]" />, iconBg: "bg-[#22c55e]/10" },
    revoke:   { title: "Revoke access?",  desc: "Their active sessions will be terminated and they'll be locked out immediately.", btn: "Revoke", color: "bg-red-600 hover:bg-red-700", icon: <AlertTriangle className="text-red-600" />, iconBg: "bg-red-50" },
    promote:  { title: "Make admin?",     desc: "They'll be able to approve, revoke, and manage other users.", btn: "Promote", color: "bg-[#0a2952] hover:bg-[#071f40]", icon: <ShieldCheck className="text-[#0a2952]" />, iconBg: "bg-[#0a2952]/10" },
    demote:   { title: "Demote admin?",   desc: "They'll lose admin privileges but keep regular access.", btn: "Demote", color: "bg-slate-700 hover:bg-slate-800", icon: <ShieldOff className="text-slate-700" />, iconBg: "bg-slate-100" },
  }[action];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid={`confirm-${action}-dialog`}>
      <div className="bg-white rounded-md w-full max-w-md border border-slate-200 shadow-2xl">
        <div className="p-6">
          <div className={`w-12 h-12 rounded-full ${config.iconBg} grid place-items-center mb-4`}>{config.icon}</div>
          <div className="font-display font-extrabold text-xl text-[#0a2952] mb-2">{config.title}</div>
          <p className="text-sm text-slate-600">
            <span className="font-semibold text-[#0a2952]">{user.name || user.email}</span> ({user.email}) — {config.desc}
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded border border-slate-300 text-sm font-semibold" data-testid="confirm-action-cancel-btn">Cancel</button>
          <button type="button" onClick={onConfirm} className={`px-4 py-2 rounded text-white text-sm font-semibold ${config.color}`} data-testid="confirm-action-confirm-btn">{config.btn}</button>
        </div>
      </div>
    </div>
  );
}
